import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { colors } from '../../utils/theme';
import { Link } from 'react-router-dom';

const HoverContainer = styled.div`
  position: relative;
  display: inline-block;
  
  &:hover {
    .project-title-bg {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1.2);
    }
    .project-subtitle {
      opacity: 1;
    }
  }
`;

const ProjectBox = styled(Link)`
  width: 600px;
  height: 400px;
  background: ${props => props.backgroundColor || 'transparent'};
  position: relative;
  transition: transform 0.2s ease-out, mask-size 0.5s ease-in-out;
  display: block;
  text-decoration: none;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url(${props => props.backgroundImage}) center/contain no-repeat;
    transition: transform 0.2s ease-out;
  }

  /* The mask uses an inline SVG.
     The polygon points cut larger triangles from the top‑left and bottom‑right */
  mask-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 600 400'><polygon fill='white' points='80,0 600,0 600,320 520,400 0,400 0,80'/></svg>");
  mask-size: 130%;
  mask-repeat: no-repeat;
  mask-position: center;

  -webkit-mask-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 600 400'><polygon fill='white' points='80,0 600,0 600,320 520,400 0,400 0,80'/></svg>");
  -webkit-mask-size: 130%;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;

  &:hover {
    mask-size: 68%;
    -webkit-mask-size: 80%;
  }

  @media (max-width: 768px) {
    width: 320px;
    height: 240px;
    mask-size: 100%;
    -webkit-mask-size: 100%;
  }

  @media (max-width: 480px) {
    width: 280px;
    height: 200px;
  }
`;

const ProjectTitleBg = styled.h1`
  position: absolute;
  top: 50% + 160px;
  left: 50%;
  /* Start centered behind the box */
  transform: translate(-50%, -50%) scale(1);
  font-size: 6rem;
  color: ${colors.titleColor};
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.5s ease, transform 0.5s ease;
  z-index: -1;
  width: 100%;
  text-align: center;
  white-space: nowrap;
  font-family: "moret-variable", serif;
`;

const ProjectSubtitle = styled.p`
  position: absolute;
  top: calc(100% - 70px);
  left: 50%;
  /* Start centered horizontally below the box */
  transform: translateX(-50%);
  font-size: 1.5rem;
  color: ${colors.subtitleColor};
  opacity: 0;
  transition: opacity 0.5s ease, transform 0.2s ease-out;
  white-space: nowrap;
  font-family: "maitree", serif;
  z-index: 1;
`;

const AnimatedProjectCard = ({ title, subtitle, image, to = '#', backgroundColor }) => {
  const containerRef = useRef(null);
  const boxRef = useRef(null);
  const subtitleRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    const box = boxRef.current;
    const subtitle = subtitleRef.current;

    if (!container || !box || !subtitle) return;

    const handleMouseMove = (e) => {
      const rect = container.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      // Normalize the distance from center (-1 to 1)
      const distanceX = (e.clientX - centerX) / (rect.width / 2);
      const distanceY = (e.clientY - centerY) / (rect.height / 2);
      // Move up to 50px in any direction
      const moveX = distanceX * 50;
      const moveY = distanceY * 50;
      // Move the box with the mouse
      box.style.transform = `translate(${moveX}px, ${moveY}px)`;
      // Adjust the subtitle while preserving its centered start position
      subtitle.style.transform = `translate(calc(-50% + ${moveX}px), ${moveY}px)`;
    };

    const handleMouseLeave = () => {
      box.style.transform = 'translate(0, 0)';
      subtitle.style.transform = 'translate(-50%, 0)';
    };

    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <HoverContainer ref={containerRef}>
      <ProjectTitleBg className="project-title-bg">{title}</ProjectTitleBg>
      <ProjectBox 
        ref={boxRef} 
        to={to} 
        backgroundImage={image}
        backgroundColor={backgroundColor}
        className="project-box" 
      />
      <ProjectSubtitle ref={subtitleRef} className="project-subtitle">
        {subtitle}
      </ProjectSubtitle>
    </HoverContainer>
  );
};

export default AnimatedProjectCard;
